import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Warehouse, Anchor, Ship, MapPin } from "lucide-react";

// Presentation-only labels over canonical statuses (state machine untouched):
// Morocco stock (MOROCCO) → Fez · DEPARTED → In Transit · ARRIVING → Arriving · Florida (FLORIDA) → Available Now.
// Casablanca is a visual departure node only — no such location or status exists in data.
// All quantities are commercial/net availability only — never internal counters.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtEta(s) {
  // date-only ISO strings ("2026-09-08") formatted without timezone drift
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-");
  if (!y || !m || !d) return s;
  return `${MONTHS[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

const labelCls = "text-xs font-semibold uppercase tracking-[0.2em] text-terracotta";
const subCls = "text-xs text-muted-foreground";
const EMPTY_COPY = {
  fez: "Nothing in preparation at the atelier right now.",
  atlantic: "No containers currently in transit.",
  miami: "Nothing arriving right now.",
  wpb: "Nothing available here right now.",
};

function Node({ testid, icon: Icon, title, sub, onClick }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={!onClick}
      className={`relative z-10 flex w-16 flex-col items-center gap-1 sm:w-20 ${onClick ? "cursor-pointer" : "cursor-default"}`}>
      <span className={`flex h-9 w-9 items-center justify-center rounded-full border sm:h-11 sm:w-11 ${onClick ? "border-terracotta bg-white text-terracotta transition-colors hover:bg-terracotta hover:text-white" : "border-limestone bg-sand text-warmgrey"}`}>
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
      </span>
      <span className="text-center text-[10px] font-semibold uppercase tracking-wider text-charcoal sm:text-xs">{title}</span>
      <span className="text-center text-[9px] text-warmgrey sm:text-[10px]">{sub}</span>
    </button>
  );
}

function EtaLine({ item, node }) {
  if (node === "fez") return <span className="text-warmgrey">Florida ETA: Not scheduled</span>;
  if (node === "wpb") return <span className="text-warmgrey">Available now</span>;
  if (!item.expected_florida) return <span className="text-warmgrey">Expected Florida availability: —</span>;
  return item.eta_is_estimate
    ? <span className="text-warmgrey">Port ETA (estimate): {fmtEta(item.expected_florida)}</span>
    : <span className="text-warmgrey">Expected Florida availability: {fmtEta(item.expected_florida)}</span>;
}

export function DealerJourney({ journey }) {
  const [drill, setDrill] = useState(null);
  const navigate = useNavigate();
  if (!journey) return null;
  const { morocco, in_transit, arriving, florida } = journey;
  const close = () => setDrill(null);

  const Items = ({ items, node }) => (
    <div className="mt-3 max-h-56 space-y-2 overflow-y-auto" data-testid="dj-drill-body">
      {items.length === 0 && <p className={subCls}>{EMPTY_COPY[node]}</p>}
      {items.map((i) => (
        <div key={i.variant_id} className="border-b border-limestone pb-2">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-charcoal">{i.label}</span>
            <span className="shrink-0 text-xs font-medium text-charcoal">{(i.available_sqft ?? 0).toLocaleString()} sq ft</span>
          </div>
          <p className="mt-0.5 text-xs"><EtaLine item={i} node={node} /></p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="mt-8" data-testid="dealer-journey">
      <p className={labelCls}>Inventory journey</p>
      <div className="relative mt-4 rounded-lg border border-border bg-white px-2 py-6 sm:px-6">
        <div className="overflow-x-auto sm:overflow-visible">
          <div className="relative min-w-[560px] sm:min-w-0">
            <div className="absolute left-[12%] right-[12%] top-[18px] border-t-2 border-dashed border-limestone sm:top-[22px]" aria-hidden="true" />
            <div className="relative flex items-start justify-between">
          <Node testid="dj-node-fez" icon={Warehouse} title="At the Atelier"
            sub={`${morocco.total_sqft.toLocaleString()} sq ft`} onClick={() => setDrill({ type: "fez" })} />
          <Node testid="dj-node-casablanca" icon={Anchor} title="Casablanca" sub="departure" />
          <Node testid="dj-node-atlantic" icon={Ship} title="In Transit"
            sub={in_transit.total_sqft ? `${in_transit.total_sqft.toLocaleString()} sq ft` : "at sea"}
            onClick={() => setDrill({ type: "atlantic" })} />
          <Node testid="dj-node-miami" icon={MapPin} title="Arriving"
            sub={arriving.total_sqft ? `${arriving.total_sqft.toLocaleString()} sq ft` : "Miami"}
            onClick={() => setDrill({ type: "miami" })} />
          <Node testid="dj-node-wpb" icon={Warehouse} title="Available Now"
            sub={`${florida.total_sqft.toLocaleString()} sq ft`} onClick={() => setDrill({ type: "wpb" })} />
            </div>
          </div>
        </div>
        <p className="mt-4 text-center text-[10px] uppercase tracking-wider text-warmgrey">
          Commercial availability across our Morocco → Florida route
        </p>
        <p className="mt-1 text-center text-[10px] text-warmgrey sm:hidden" aria-hidden="true">← swipe to explore the route →</p>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md sm:!max-h-[85dvh] sm:!overflow-y-auto" data-testid="dj-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-medium text-charcoal" data-testid="dj-drill-title">
              {drill?.type === "fez" && "At the atelier — in preparation"}
              {drill?.type === "atlantic" && "In transit — Atlantic crossing"}
              {drill?.type === "miami" && "Arriving — Miami"}
              {drill?.type === "wpb" && "Available now — Florida warehouse"}
            </DialogTitle>
          </DialogHeader>
          {drill?.type === "wpb" && (
            <p className={subCls} data-testid="dj-wpb-copy">
              Typically ships in 2–5 business days — typical shipment window, not a guaranteed delivery date.
            </p>
          )}
          {drill?.type === "fez" && (
            <p className={subCls}>Inventory at our Morocco atelier. Not yet scheduled on a departing container.</p>
          )}
          {drill && <Items node={drill.type} items={
            drill.type === "fez" ? morocco.items
            : drill.type === "atlantic" ? in_transit.items
            : drill.type === "miami" ? arriving.items
            : florida.items
          } />}
          <button type="button" data-testid="dj-drill-link" onClick={() => { close(); navigate("/dealer/discover"); }}
            className="mt-4 text-xs font-semibold uppercase tracking-wider text-terracotta hover:text-terracotta-hover">
            Browse products →
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
